import { useCallback, useEffect, useMemo, useState } from "react";

import {
  APIError,
  generateMapMockTraffic,
  getMockNarrowPassageScenario,
  getDevices,
  getMockTrafficOverview,
  resetMapMockTraffic,
  setMockNarrowPassageScenario,
  type Device,
  type MapMockTraffic,
  type MockTrafficOverview,
} from "./api";
import { LocalizedDateInput } from "./LocalizedDateInput";
import { formatISODate, formatNumber, structuralDateParts } from "./localization";

const maximumGenerationDays = 31;
const mockTimezone = "Europe/Vienna";

function currentDateInTimezone(timeZone: string) {
  const parts = structuralDateParts(new Date(), timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function inclusiveDayCount(fromDate: string, toDate: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate) || !/^\d{4}-\d{2}-\d{2}$/.test(toDate)) return Number.NaN;
  const from = Date.parse(`${fromDate}T00:00:00.000Z`);
  const to = Date.parse(`${toDate}T00:00:00.000Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to) || new Date(from).toISOString().slice(0, 10) !== fromDate || new Date(to).toISOString().slice(0, 10) !== toDate) return Number.NaN;
  return Math.floor((to - from) / 86_400_000) + 1;
}

function mockTrafficSummary(value: MapMockTraffic | null, stale: boolean) {
  if (!value) return "Keine Mockdaten erzeugt.";
  const range = value.from_date === value.to_date ? formatISODate(value.from_date) : `${formatISODate(value.from_date)} bis ${formatISODate(value.to_date)}`;
  if (stale) return `${range} · gespeichert, wegen geänderter Messliniengeneration derzeit ausgeblendet.`;
  return `${range} · ${formatNumber(value.bucket_count)} Viertelstunden · Generation ${value.configuration_generation}`;
}

const narrowPassageScenarioLabels: Record<string, string> = {
  baseline: "Normalbetrieb",
  stress: "Belastung: mehr Fehlklassen und Verluste, weniger Kennzeichenlesungen",
};

// The scenario switch exists only in the local mock API; against a real
// backend the request fails and the section stays hidden.
function NarrowPassageScenario({ onSignedOut }: { onSignedOut: () => void }) {
  const [state, setState] = useState<{ scenario: string; scenarios: string[] }>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getMockNarrowPassageScenario().then((value) => { if (active && Array.isArray(value.scenarios)) setState(value); }).catch((reason: unknown) => { if (active && reason instanceof APIError && reason.status === 401) onSignedOut(); });
    return () => { active = false; };
  }, [onSignedOut]);

  async function select(scenario: string) {
    setSaving(true); setError("");
    try { setState(await setMockNarrowPassageScenario(scenario)); } catch (reason) {
      if (reason instanceof APIError && reason.status === 401) onSignedOut();
      else setError("Das Engstellen-Szenario konnte nicht umgeschaltet werden.");
    } finally { setSaving(false); }
  }

  if (!state) return null;
  return <section className="mock-data-generator mock-data-narrow-passage" aria-label="Engstellen-Szenario">
    <div className="mock-data-generator-heading"><h2>Engstellen-Szenario</h2><p>Vorberechnete Auswertungen des Beispielverbunds unter „Messstellenverbünde“. Nur in der lokalen Vorschau.</p></div>
    <label>Szenario<select value={state.scenario} disabled={saving} onChange={(event) => void select(event.target.value)}>{state.scenarios.map((scenario) => <option key={scenario} value={scenario}>{narrowPassageScenarioLabels[scenario] ?? scenario}</option>)}</select></label>
    {error && <small className="mock-data-error">{error}</small>}
  </section>;
}

export function MockDataSetup({ narrowPassageEnabled = false, onSignedOut, onMockGenerated, selectedMeasurementPointID, simulationMeasurementPointID }: { narrowPassageEnabled?: boolean; onSignedOut: () => void; onMockGenerated: (generated: { measurementPointID: string; fromDate: string; toDate: string }) => void; selectedMeasurementPointID?: string; simulationMeasurementPointID: string }) {
  const defaultDate = currentDateInTimezone(mockTimezone);
  const [points, setPoints] = useState<Device[]>([]);
  const [overview, setOverview] = useState<MockTrafficOverview[]>([]);
  const [pointID, setPointID] = useState(selectedMeasurementPointID ?? "");
  const [fromDate, setFromDate] = useState(defaultDate);
  const [toDate, setToDate] = useState(defaultDate);
  const [intensity, setIntensity] = useState<MapMockTraffic["intensity"]>("normal");
  const [seed, setSeed] = useState("graz-verkehr");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const handleError = useCallback((reason: unknown, fallback: string) => {
    if (reason instanceof APIError && reason.status === 401) {
      onSignedOut();
      return;
    }
    setError(fallback);
  }, [onSignedOut]);

  const load = useCallback(() => Promise.all([getDevices(), getMockTrafficOverview()]), []);

  const reload = useCallback(async () => {
    const [nextPoints, nextOverview] = await load();
    const simulationPoints = nextPoints.filter((point) => point.id === simulationMeasurementPointID);
    setPoints(simulationPoints);
    setOverview(nextOverview);
    setPointID(simulationPoints[0]?.id || "");
  }, [load, simulationMeasurementPointID]);

  useEffect(() => {
    let active = true;
    load().then(([nextPoints, nextOverview]) => {
      if (!active) return;
      const simulationPoints = nextPoints.filter((point) => point.id === simulationMeasurementPointID);
      setPoints(simulationPoints);
      setOverview(nextOverview);
      setPointID(simulationPoints[0]?.id || "");
    }).catch((reason: unknown) => { if (active) handleError(reason, "Mockdaten und Messstellen konnten nicht geladen werden."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [handleError, load, selectedMeasurementPointID, simulationMeasurementPointID]);

  const overviewByPointID = useMemo(() => new Map(overview.map((item) => [item.measurement_point_id, item])), [overview]);
  const selectedOverview = overviewByPointID.get(pointID);
  const generatedCount = overview.filter((item) => item.mock_traffic).length;
  const dayCount = inclusiveDayCount(fromDate, toDate);
  const rangeError = !Number.isFinite(dayCount) || dayCount < 1
    ? "Bitte ein gültiges Start- und Enddatum auswählen."
    : dayCount > maximumGenerationDays
      ? `Eine lokale Generierung umfasst höchstens ${maximumGenerationDays} Kalendertage.`
      : "";

  async function generate() {
    if (!pointID || !selectedOverview || rangeError || !seed.trim()) return;
    setGenerating(true); setError(""); setNotice("");
    try {
      const generated = await generateMapMockTraffic(pointID, selectedOverview.configuration_generation, fromDate, toDate, intensity, seed.trim());
      await reload();
      onMockGenerated({ measurementPointID: pointID, fromDate: generated.from_date, toDate: generated.to_date });
    } catch (reason) {
      await reload().catch(() => undefined);
      handleError(reason, "Die Mockdaten konnten nicht erzeugt werden. Bitte Zeitraum, Messliniengeneration und Seed prüfen.");
    } finally { setGenerating(false); }
  }

  async function resetAll() {
    if (!generatedCount || !window.confirm(`Sämtliche Mockdaten für ${generatedCount} Messstelle${generatedCount === 1 ? "" : "n"} löschen? Die lokalen Daten können nur durch eine neue Generierung wiederhergestellt werden.`)) return;
    setResetting(true); setError(""); setNotice("");
    try {
      const resetCount = await resetMapMockTraffic();
      await reload();
      setNotice(`Mockdaten für ${resetCount} Messstelle${resetCount === 1 ? "" : "n"} wurden zurückgesetzt.`);
    } catch (reason) {
      handleError(reason, "Die Mockdaten konnten nicht zurückgesetzt werden.");
    } finally { setResetting(false); }
  }

  return <main className="management-page mock-data-page">
    <div className="dashboard-heading management-heading"><div><h1>Mockdaten</h1><p><strong>SIMULATIONSDATEN</strong> · Reproduzierbare Verkehrsprofile der serverseitigen Sandbox-Simulation. Sie umgehen Pi-Agent, MQTT und reale Geräte vollständig.</p></div></div>
    <section className="mock-data-intro"><div><strong>Zeitraumprofile</strong><p>Werktage enthalten Pendler- und Arbeitszeitspitzen. An Wochenenden sinkt das Gesamtaufkommen deutlich; Lkw und Transporter werden zusätzlich stark reduziert.</p></div><div><strong>Persistente Sandbox</strong><p>Eine neue Generierung ersetzt ausschließlich den bisherigen Simulationsstand der gewählten Simulationsmessstelle. Andere Mandanten und reale Messdaten bleiben unberührt.</p></div></section>
    {error && <p className="page-error" role="alert">{error}</p>}
    {notice && <p className="page-notice" role="status">{notice}</p>}
    {loading ? <p className="subtle-loading">Mockdaten werden geladen …</p> : <>
      <section className="mock-data-generator" aria-label="Mockdaten erzeugen">
        <div className="mock-data-generator-heading"><h2>Mockdaten erzeugen</h2><p>Das Enddatum wird vollständig einbezogen. Maximal {maximumGenerationDays} Kalendertage pro serverseitiger Generierung.</p></div>
        <label>Messstelle<select value={pointID} onChange={(event) => { setPointID(event.target.value); setError(""); setNotice(""); }}>{points.map((point) => <option key={point.id} value={point.id}>{point.location}</option>)}</select></label>
        <label>Startdatum<LocalizedDateInput id="mock-from-date" label="Startdatum" max={toDate || defaultDate} value={fromDate} onChange={setFromDate} /></label>
        <label>Enddatum<LocalizedDateInput id="mock-to-date" label="Enddatum" min={fromDate} max={defaultDate} value={toDate} onChange={setToDate} /></label>
        <label>Verkehrsstärke<select value={intensity} onChange={(event) => setIntensity(event.target.value as MapMockTraffic["intensity"])}><option value="quiet">Ruhig</option><option value="normal">Normal</option><option value="busy">Stark</option></select></label>
        <label>Seed<input value={seed} maxLength={64} onChange={(event) => setSeed(event.target.value)} /></label>
        <div className="mock-data-generator-actions"><button type="button" className="primary-button" disabled={!pointID || Boolean(rangeError) || generating || !seed.trim()} onClick={() => void generate()}>{generating ? "Generiert …" : "Generieren und im Dashboard anzeigen"}</button>{rangeError ? <small className="mock-data-error">{rangeError}</small> : <small>{dayCount} {dayCount === 1 ? "Kalendertag" : "Kalendertage"} · {selectedOverview ? mockTrafficSummary(selectedOverview.mock_traffic, selectedOverview.mock_traffic_stale) : "Messstelle wird geladen."}</small>}</div>
      </section>
      <section className="mock-data-overview"><div className="mock-data-overview-heading"><div><h2>Gespeicherte Simulationsdaten</h2><p>{generatedCount ? `${generatedCount} von ${points.length} Simulationsmessstellen enthalten Daten.` : "Derzeit sind keine Simulationsdaten gespeichert."}</p></div><button type="button" className="danger-button" disabled={!generatedCount || resetting} onClick={() => void resetAll()}>{resetting ? "Setzt zurück …" : "Sämtliche Simulationsdaten rücksetzen"}</button></div>
        <ul>{points.map((point) => {
          const entry = overviewByPointID.get(point.id);
          return <li key={point.id} className={point.id === pointID ? "selected" : ""}><button type="button" onClick={() => { setPointID(point.id); setError(""); setNotice(""); }}><strong>{point.location}</strong><small>{entry ? mockTrafficSummary(entry.mock_traffic, entry.mock_traffic_stale) : "Mockdatenstatus wird geladen."}</small></button></li>;
        })}</ul>
      </section>
      {narrowPassageEnabled && <NarrowPassageScenario onSignedOut={onSignedOut} />}
    </>}
  </main>;
}
